import {
  IQuestionnaire,
  QuestionnaireType,
  IQuestionnaireExtension,
  IQuestionnaireExpression,
   IQuestionnaireCodeableConcept,
   IQuestionnaireCoding,
   IQuestionnaireOption,
} from "./IQuestionnaire";

export class Questionnaire {
  constructor(
    public _guid: string,
    public _childGuids: string[],
    public type: QuestionnaireType | undefined,
    public linkId: string,
    public text: string,
    public repeats?: boolean,
    public maxOccurs?: number,
    public fhirPath?: QuestionnaireExtension,
    public display?: string,
    public code?: string,
    public gtable?: QuestionnaireExtension,
    public answerOption?: QuestionnaireOption[],
    ) {
  }

  clone(): Questionnaire {
    return new Questionnaire(
      this._guid,
      this._childGuids,
      this.type,
      this.linkId,
      this.text,
      this.repeats,
      this.maxOccurs,
      this.fhirPath ? this.fhirPath.clone() : undefined,
      this.display,
      this.code,
      this.gtable ? this.gtable.clone() : undefined,
      this.answerOption ? this.answerOption.map(o => o.clone()) : undefined);
  }

  static from(source: IQuestionnaire): Questionnaire {
    return new Questionnaire(
      source._guid,
      source._childGuids,
      source.type,
      source.linkId,
      source.text,
      source.repeats,
      source.maxOccurs,
      source.fhirPath ? QuestionnaireExtension.from(source.fhirPath) : undefined,
      source.display,
      source.code,
      source.gtable ? QuestionnaireExtension.from(source.gtable) : undefined,
      source.answerOption ? source.answerOption.map(o => QuestionnaireOption.from(o)) : undefined);
  }

  static to(source: Questionnaire): IQuestionnaire {
    return {
      _guid: source._guid,
      _childGuids: source._childGuids,
      type: source.type,
      linkId: source.linkId,
      text: source.text,
      repeats: source.repeats,
      maxOccurs: source.maxOccurs,
      fhirPath: source.fhirPath ? QuestionnaireExtension.to(source.fhirPath) : undefined,
      display: source.display,
      code: source.code,
      gtable: source.gtable ? QuestionnaireExtension.to(source.gtable) : undefined,
      answerOption: source.answerOption ? source.answerOption.map(o => QuestionnaireOption.to(o)) : undefined,
    };
  }
}

export class QuestionnaireExtension {
  constructor(
    public url: string,
    public valueExpression?: QuestionnaireExpression,
    public valueCodeableConcept?: QuestionnaireCodeableConcept,
  ) {
  }

  clone(): QuestionnaireExtension {
    return new QuestionnaireExtension(
      this.url,
      this.valueExpression ? this.valueExpression.clone() : this.valueExpression,
      this.valueCodeableConcept ? this.valueCodeableConcept.clone() : this.valueCodeableConcept);
  }

  static from(source: IQuestionnaireExtension): QuestionnaireExtension {
    return new QuestionnaireExtension(
      source.url,
      source.valueExpression ? QuestionnaireExpression.from(source.valueExpression) : undefined,
      source.valueCodeableConcept ? QuestionnaireCodeableConcept.from(source.valueCodeableConcept) : undefined);
  }

  static to(source: QuestionnaireExtension): IQuestionnaireExtension {
    return {
      url: source.url,
      valueExpression: source.valueExpression ? QuestionnaireExpression.to(source.valueExpression) : undefined,
      valueCodeableConcept: source.valueCodeableConcept ? QuestionnaireCodeableConcept.to(source.valueCodeableConcept) : undefined,
    };
  }
}

export class QuestionnaireExpression {
  constructor(
    public expression: string,
    public name: string,
    public language: string,
  ) {
  }

  clone(): QuestionnaireExpression {
    return new QuestionnaireExpression(this.expression, this.name, this.language);
  }

  static from(source: IQuestionnaireExpression): QuestionnaireExpression {
    return new QuestionnaireExpression(source.expression, source.name, source.language);
  }

  static to(source: QuestionnaireExpression): IQuestionnaireExpression {
    return {
      name: source.name,
      language: source.language,
      expression: source.expression,
    };
  }
}

export class QuestionnaireCodeableConcept {
  constructor(
    public coding: QuestionnaireCoding[],
    public text?: string,
  ) {
  }

  clone(): QuestionnaireCodeableConcept {
    return new QuestionnaireCodeableConcept(this.coding, this.text);
  }

  static from(source: IQuestionnaireCodeableConcept): QuestionnaireCodeableConcept {
    return new QuestionnaireCodeableConcept(source.coding.map(c => QuestionnaireCoding.from(c)), source.text);
  }

  static to(source: QuestionnaireCodeableConcept): IQuestionnaireCodeableConcept {
    return {
      coding: source.coding.map(c => QuestionnaireCoding.to(c)),
      text: source.text,
    };
  }
}

export class QuestionnaireOption {
  constructor(
    public valueCoding: QuestionnaireCoding,
  ) {
  }

  clone(): QuestionnaireOption {
    return new QuestionnaireOption(this.valueCoding);
  }

  static from(source: IQuestionnaireOption): QuestionnaireOption {
    return new QuestionnaireOption(QuestionnaireCoding.from(source.valueCoding));
  }

  static to(source: QuestionnaireOption): IQuestionnaireOption {
    return {
      valueCoding: QuestionnaireCoding.to(source.valueCoding),
    };
  }
}

export class QuestionnaireCoding {
  constructor(
    public code: string,
    public system?: string,
    public display?: string,
  ) {
  }

  clone(): QuestionnaireCoding {
    return new QuestionnaireCoding(this.code, this.system, this.display);
  }

  static from(source: IQuestionnaireCoding): QuestionnaireCoding {
    return new QuestionnaireCoding(source.code, source.system, source.display);
  }

  static to(source: QuestionnaireCoding): IQuestionnaireCoding {
    return {
      code: source.code,
      system: source.system,
      display: source.display,
    };
  }
}
